import React from "react";
import { Node } from "@tiptap/core";
import { useEditor, EditorContent } from "@tiptap/react";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import History from "@tiptap/extension-history";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import Popover from "@mui/material/Popover";

/**
 * Document node. We define it inline rather than importing
 * `@tiptap/extension-document` because that specific package isn't in
 * node_modules — only the individual extensions BlockNote uses
 * transitively are. Definition matches Tiptap's official Document.
 */
const Document = Node.create({
  name: "doc",
  topNode: true,
  content: "block+",
});

/**
 * Constrained Tiptap editor for swipe-card body text. Inputs and outputs
 * HTML strings; the player and storyboard preview render via
 * `dangerouslySetInnerHTML`.
 *
 * Schema is intentionally tight — paragraph + bold + italic, plus
 * History (undo/redo) and HardBreak. No headings, lists, blockquotes,
 * or block-level elements: cards aren't documents, and a wide-open
 * editor would let admins author content that breaks the card layout.
 *
 * Per-paragraph text alignment is intentionally NOT in this editor.
 * It would require @tiptap/extension-text-align (not in our deps).
 * Card-level alignment (whole card text centered, etc.) lives on the
 * card content as a separate `text_align` field — see the editor's
 * "Text alignment" segmented control.
 *
 * Empty-state value is `""`; the editor outputs `<p></p>` for an empty
 * paragraph, which we normalize to `""` so card-level "is body present?"
 * checks can stay simple.
 */
export function RichTextField({
  value,
  onChange,
  placeholder,
  toolbarTrailing,
  emojiOptions,
}) {
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Bold,
      Italic,
      History,
      BulletList,
      OrderedList,
      ListItem,
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      const normalized =
        html === "<p></p>" || html === "<p><br></p>" ? "" : html;
      onChange(normalized);
    },
    editorProps: {
      attributes: {
        // Tailwind utilities applied directly to ProseMirror's DOM:
        //   - reset paragraph margins (`[&_p]:my-0`) so card layouts stay tight
        //   - readable line height + size matching the rest of the form
        //   - `focus:outline-none` so Tiptap's default outline doesn't
        //     fight the wrapper's focus ring
        class:
          "px-3 py-2 text-sm leading-snug min-h-[80px] focus:outline-none [&_p]:my-0 [&_p+p]:mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-0 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-0 [&_li]:my-0 [&_li>p]:my-0",
      },
    },
  });

  // Resync external `value` changes (e.g., switching cards) — without this
  // the editor keeps showing the old card's content when the parent swaps
  // which card is selected. Skip the resync when the change came from
  // *inside* the editor (which would clobber the cursor mid-keystroke).
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || "";
    if (current === incoming) return;
    if (current === "<p></p>" && incoming === "") return;
    editor.commands.setContent(incoming, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  // When the editor has never been focused (fresh field, no text yet),
  // focusing without an explicit position can leave the cursor at index 0
  // — before the first paragraph node — where Tiptap's stored marks
  // don't take. Forcing focus to "end" the first time guarantees the
  // cursor lands inside a real text position so the bold/italic mark
  // sticks for the next typed character.
  const focusForToggle = (chain) =>
    editor.isFocused ? chain.focus() : chain.focus("end");

  return (
    <div className="rounded-lg border border-slate-200 bg-white focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100">
      <div className="flex items-center gap-0.5 border-b border-slate-200 px-2 py-1">
        <RichTextToolbarButton
          onClick={() => focusForToggle(editor.chain()).toggleBold().run()}
          active={editor.isActive("bold")}
          aria-label="Bold"
        >
          <FormatBoldIcon style={{ fontSize: 16 }} />
        </RichTextToolbarButton>
        <RichTextToolbarButton
          onClick={() => focusForToggle(editor.chain()).toggleItalic().run()}
          active={editor.isActive("italic")}
          aria-label="Italic"
        >
          <FormatItalicIcon style={{ fontSize: 16 }} />
        </RichTextToolbarButton>
        <RichTextToolbarButton
          onClick={() => focusForToggle(editor.chain()).toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          aria-label="Bulleted list"
        >
          <FormatListBulletedIcon style={{ fontSize: 16 }} />
        </RichTextToolbarButton>
        <RichTextToolbarButton
          onClick={() => focusForToggle(editor.chain()).toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          aria-label="Numbered list"
        >
          <FormatListNumberedIcon style={{ fontSize: 16 }} />
        </RichTextToolbarButton>
        {emojiOptions && emojiOptions.length > 0 && (
          <EmojiPickerButton
            emojis={emojiOptions}
            onPick={(emoji) =>
              focusForToggle(editor.chain()).insertContent(emoji).run()
            }
          />
        )}
        {toolbarTrailing && (
          <div className="ml-auto flex items-center">{toolbarTrailing}</div>
        )}
      </div>
      <div className="relative">
        <EditorContent editor={editor} />
        {!value && placeholder && (
          <div className="pointer-events-none absolute top-2 left-3 text-sm text-slate-400">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Button that opens a grid of quick emojis and calls `onPick(emoji)` with the
 * chosen one. Serves two roles off the same popover:
 *   - Inserter (toolbars): trigger shows a face icon; the caller inserts the
 *     emoji at the cursor (Tiptap cursor for the body, input caret for the
 *     headline).
 *   - Single-value selector (the card's emoji prefix): pass `selected` so the
 *     trigger shows the current emoji, and `onClear` to expose a "No emoji"
 *     option in the popover.
 * Styled to match the other toolbar buttons.
 */
export function EmojiPickerButton({
  emojis,
  onPick,
  selected = null,
  onClear,
  ariaLabel = "Insert emoji",
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  return (
    <>
      <RichTextToolbarButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        active={open}
        aria-label={ariaLabel}
      >
        {selected ? (
          <span className="text-base leading-none">{selected}</span>
        ) : (
          <EmojiEmotionsOutlinedIcon style={{ fontSize: 16 }} />
        )}
      </RichTextToolbarButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        // Don't restore focus to the trigger on close — the headline emoji
        // insert restores the caret to the <input> via rAF, and MUI's default
        // focus-restore would race/win that and steal focus back to the button.
        disableRestoreFocus
      >
        <div className="p-2 w-[280px]">
          {onClear && (
            <button
              type="button"
              onClick={() => {
                onClear();
                setAnchorEl(null);
              }}
              className={[
                "mb-1.5 w-full rounded-md px-2 py-1 text-left text-xs font-medium transition-colors",
                selected
                  ? "text-slate-600 hover:bg-slate-100"
                  : "bg-violet-50 text-violet-700",
              ].join(" ")}
            >
              No emoji
            </button>
          )}
          <div className="grid grid-cols-8 gap-1">
            {emojis.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  onPick(emoji);
                  setAnchorEl(null);
                }}
                className={[
                  "w-8 h-8 rounded-lg text-base flex items-center justify-center transition-colors",
                  selected === emoji
                    ? "bg-violet-100 ring-2 ring-violet-600"
                    : "hover:bg-slate-100",
                ].join(" ")}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      </Popover>
    </>
  );
}

// Exported so other card-text editors (e.g. the headline field, which is a
// plain input but wants a matching B/I toolbar) can reuse the exact button.
export function RichTextToolbarButton({ onClick, active, children, ...rest }) {
  return (
    <button
      type="button"
      // Prevent the browser's default mousedown behavior from moving focus
      // off the editor. Without this, clicking Bold blurs the editor before
      // onClick fires — so there's no cursor position to apply the toggle
      // to, and Tiptap can't set the "next character is bold" stored mark.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={[
        "w-7 h-7 rounded flex items-center justify-center transition-colors",
        active
          ? "bg-violet-100 text-violet-700"
          : "text-slate-500 hover:text-slate-900 hover:bg-slate-100",
      ].join(" ")}
      {...rest}
    >
      {children}
    </button>
  );
}
